import { HostAppName, HostAppSettingName, HostAppSettings } from "../office-js/HostAppSettings";
import { OneNotePageError, OneNotePageErrors } from "./OneNotePageErrors";
import { ErrorStringFromException, getDateString, GetImageBuffer, isNullOrUndefined, PromiseWithRetry } from "../../utils/utils";
import dispatcher from "../../frontend/dispatcher";
import { HostAppUtil } from "../office-js/HostAppUtil";
import { HostAppEventHandlerFactory } from "../factory/HostAppEventHandlerFactory";
import { HostAppEvent } from "../manager/eventManager/HostAppEventReceiver";
import { TranscriptionActionTypes } from "../../frontend/stores/utils";
import { ITranscriptionSourceFile } from "../interfaces/ITranscriptionSourceFileProvider";
import { AppLayoutSettings } from "../../frontend/common/styles/common.styles";
import { TranscriptionAlertActions, TranscriptionAlertButtonType, TranscriptionAlertState } from "../../frontend/components/Popups/TranscriptionAlert";
import { COMMA, EMPTY_STRING, LEARN_MORE_URL, LINKED_PAGE_INFO_LOAD_RETRY_DELAY_MILLISECONDS, MAX_LINKED_PAGE_INFO_LOAD_RETRY_ALLOWED, TRANSCRIPTION_SIGNAL_INDICATOR_PATH } from "../../AppConstants";
import { IAddTranscriptionIndicatorToHost } from "../interfaces/IAddTranscriptionIndicatorToHost";
import { LogError } from "../../utils/ErrorTelemetry";
import { LogEventNames } from "../interfaces/ITelemetryLogger";

export const OneNoteManagerActionTypes = {
  OneNoteAssociatedPageUpdate: 'ONE_NOTE_ASSOCIATED_PAGE_UPDATE'
};

class OneNoteTranscriptionManager implements IAddTranscriptionIndicatorToHost {

  private static notebookNameKey: string = 'name';
  private static sectionNameKey: string = 'name';
  private static pageNameKey: string = 'title';

  private associatedPageInfo: AssociatedPageInfo;

  private indicatorImageBuffer: string | ArrayBuffer | null = null;

  constructor() {
    this.associatedPageInfo = new AssociatedPageInfo();
  }

  public Initialize(): void {
    HostAppEventHandlerFactory.GetInstance().on(HostAppEvent[HostAppEvent.ONENOTE_PAGE_CHANGED], this.onPageChange);
    HostAppEventHandlerFactory.GetInstance().on(HostAppEvent[HostAppEvent.ONENOTE_PAGE_MOVED], this.onAssociatedPageMoved);
    HostAppEventHandlerFactory.GetInstance().on(HostAppEvent[HostAppEvent.ONENOTE_PAGE_DELETED], this.onPageDeleted);


    /*Need to handle refresh case, delete the page and popup comes on screen, now right click on Transcription Pane and refresh, all intances are created again.
      also delay of 300 msec is added as sometime event was getting triggered before registerant register to dispatcher and event is missed.*/
    if (this.IsAssociatedPageAvailable()) {

      /*Retry attempt should be made for finite amount of time as page info is rquired
      for proper working of navigation to linked oneNote page.*/
      PromiseWithRetry(
        this.SyncAssociatedPageInfoFromOneNoteAndNotifyIfRequired,
        MAX_LINKED_PAGE_INFO_LOAD_RETRY_ALLOWED,
        LINKED_PAGE_INFO_LOAD_RETRY_DELAY_MILLISECONDS
      ).catch((e) => {
        LogError(LogEventNames.LoadLinkedOneNotePageInfo, "Linked page info load failed with error " + ErrorStringFromException(e));
      });
    } else {
      setTimeout(() => { this.onPageDeleted(HostAppSettings.GetSettingAsString(HostAppSettingName.AssociatedOneNotePageId)) }, 300);
    }
  }

  public DeInitialize(): void {
    HostAppEventHandlerFactory.GetInstance().off(HostAppEvent[HostAppEvent.ONENOTE_PAGE_CHANGED], this.onPageChange);
    HostAppEventHandlerFactory.GetInstance().off(HostAppEvent[HostAppEvent.ONENOTE_PAGE_MOVED], this.onAssociatedPageMoved);
    HostAppEventHandlerFactory.GetInstance().off(HostAppEvent[HostAppEvent.ONENOTE_PAGE_DELETED], this.onPageDeleted);
  }

  public async AddTranscriptionIndicator(sourceFile: ITranscriptionSourceFile): Promise<void> {
    if (isNullOrUndefined(sourceFile)) {
      throw new OneNotePageError(OneNotePageErrors.AddTranscriptionIndicatorToOneNoteFailure, "can not add indicator for null or underfined ITranscriptionSourceFile");
    }

    // If indicatorImageBuffer is not available, generate indicatorImageBuffer.
    if (isNullOrUndefined(this.indicatorImageBuffer)) {
      try {
        this.indicatorImageBuffer = await GetImageBuffer(TRANSCRIPTION_SIGNAL_INDICATOR_PATH);
      } catch (e) {
        throw new OneNotePageError(OneNotePageErrors.AddTranscriptionIndicatorToOneNoteFailure, "failed to fetch image buffer");
      }
    }

    // create Trancription Indicator html and post to oneNote Page, which is linked with Transcription pane.
    try {
      const audioNameWithURL = "<a href=" + sourceFile?.permanentAudioUrl + ">" + sourceFile!.fileName + "</a>";
      const information = audioNameWithURL + COMMA + TranscriptionStrings.OneNotePageIndicatorText;

      const html = "<img src=" + this.indicatorImageBuffer + "width=\"30\" height=\"30\"> </img>" +
        "<p style = color:" + AppLayoutSettings.getAppColorContainer().transcriptionIndicatorTextColor.value + ";" + "font-size:" + AppLayoutSettings.AppFontSize.mediumFontSizePx + ";" + ">" + information + "</p>" +
        "<p style= color:" + AppLayoutSettings.getAppColorContainer().transcriptionIndicatorTextColor.value + ";" + "font-size:" + AppLayoutSettings.AppFontSize.mediumFontSizePx + ";" + ">" + getDateString() + "</p>"

      // Post the html content to OneNote page, linked with transcription
      await OneNote.run(async (context) => {
        await NavigateToAssociatedPage();
        context.application.insertHtmlAtCurrentPosition(html);
        context.sync();
      });
    } catch (e) {
      throw new OneNotePageError(OneNotePageErrors.AddTranscriptionIndicatorToOneNoteFailure, ErrorStringFromException(e));
    }
  }

  private onPageChange = (oldPageId: string, newPageId: string) => {

    /*During page Delete, Page change event is also triggered from one
    note as context move to other available page, but we need to discard same*/
    if (!this.IsAssociatedPageAvailable()) {
      return;
    }
    const associatedPageId = HostAppSettings.GetSettingAsString(HostAppSettingName.AssociatedOneNotePageId);
    if (newPageId == associatedPageId || oldPageId == associatedPageId) {
      this.SyncAssociatedPageInfoFromOneNoteAndNotifyIfRequired().catch((e) => {
        LogError(LogEventNames.LoadLinkedOneNotePageInfo, "Linked page info load failed with error " + ErrorStringFromException(e));
      });
    }

    if (newPageId == HostAppSettings.GetSettingAsString(HostAppSettingName.AssociatedOneNotePageId)) {
      dispatcher.dispatch({
        type: TranscriptionActionTypes.ShareCurrentTranscriptionState
      });
    }
  }

  private onPageDeleted = (pageId: string) => {
    if (pageId == HostAppSettings.GetSettingAsString(HostAppSettingName.AssociatedOneNotePageId)) {
      this.DeInitialize();
      HostAppSettings.Set(HostAppSettingName.IsAssociatedPageDeleted, true);

      //Show Info dialog that associated page is no more available.
      const transcriptionAlertState: TranscriptionAlertState = {
        information: TranscriptionStrings.OneNotePageNotAvailableMessage,
        title: TranscriptionStrings.OneNotePageNotAvailableTitle,
        buttonInfo: { buttonType: TranscriptionAlertButtonType.NONE }
      }

      dispatcher.dispatch({
        type: TranscriptionAlertActions.ShowTranscriptionAlert,
        payload: transcriptionAlertState
      });
    }
  }

  //This method should be called only in case Associated page, section has been moved to different section or notebook.
  private onAssociatedPageMoved = (newPageId: string, newSectionId: string, newNotebookId: string) => {
    HostAppSettings.Set(HostAppSettingName.AssociatedOneNotePageId, newPageId);
    HostAppSettings.Set(HostAppSettingName.AssociatedOneNoteSectionId, newSectionId);
    HostAppSettings.Set(HostAppSettingName.AssociatedOneNoteNotebookId, newNotebookId);
  }

  /* This method sync the latest oneNote page, section and notebook name,
  and dispatch an event in case of any change*/
  private SyncAssociatedPageInfoFromOneNoteAndNotifyIfRequired = async () => {

    const oldPageInfo = AssociatedPageInfo.clone(this.associatedPageInfo);
    await this.LoadLatestInfoForAssociatedPage();
    if (this.IsPageInfoChanged(oldPageInfo)) {

      //Notify listeners as OneNote page info has changed.
      dispatcher.dispatch({
        type: OneNoteManagerActionTypes.OneNoteAssociatedPageUpdate,
      });
    }
  }

  private IsPageInfoChanged(oldPageInfo: AssociatedPageInfo): boolean {
    if (isNullOrUndefined(oldPageInfo) || (oldPageInfo.notebookName !== this.associatedPageInfo.notebookName ||
      oldPageInfo.sectionName !== this.associatedPageInfo.sectionName ||
      oldPageInfo.pageName !== this.associatedPageInfo.pageName)) {
      return true;
    }
    return false;
  }

  private async LoadLatestInfoForAssociatedPage() {
    await OneNote.run(async (context) => {
      try {
        const notebook = context.application.notebooks.getItem(HostAppSettings.GetSettingAsString(HostAppSettingName.AssociatedOneNoteNotebookId));
        const section = notebook.sections.getItem(HostAppSettings.GetSettingAsString(HostAppSettingName.AssociatedOneNoteSectionId));
        const page = section.pages.getItem(HostAppSettings.GetSettingAsString(HostAppSettingName.AssociatedOneNotePageId));

        //Load notebook, section and page name.
        notebook.load(OneNoteTranscriptionManager.notebookNameKey);
        section.load(OneNoteTranscriptionManager.sectionNameKey);
        page.load(OneNoteTranscriptionManager.pageNameKey);
        await context.sync();

        //Read the loaded information to pageInfo object.
        this.associatedPageInfo.notebookName = notebook.name;
        this.associatedPageInfo.sectionName = section.name;
        this.associatedPageInfo.pageName = page.title;
      } catch (e) {
        throw new OneNotePageError(OneNotePageErrors.LoadLatestInfoForAssociatedPageFailure, ErrorStringFromException(e));
      }
    });
  }

  public GetAssociatedNoteBookName() {
    return this.associatedPageInfo.notebookName; //associatedNoteBookName
  }

  public async RefreshPageInfoRequest() {
    return new Promise<void>(function (resolve, reject) {
      oneNoteTranscriptionManager.SyncAssociatedPageInfoFromOneNoteAndNotifyIfRequired().then(() => {
        resolve();
      }).catch((e) => {
        reject(e);
      });
    });
  }

  public GetAssociatedSectionName() {
    return this.associatedPageInfo.sectionName; //associatedSectionName
  }

  public GetAssociatedPageName() {
    return this.associatedPageInfo.pageName; //associatedPageName
  }

  public IsAssociatedPageAvailable = () => {
    return !HostAppSettings.IsSettingTrue(HostAppSettingName.IsAssociatedPageDeleted);
  }
}

class AssociatedPageInfo {

  notebookName: string;
  sectionName: string;
  pageName: string;

  constructor(notebookName: string = '', sectionName: string = '', pageName: string = '') {
    this.notebookName = notebookName;
    this.sectionName = sectionName;
    this.pageName = pageName;
  }

  static clone(associatedPageInfo: AssociatedPageInfo): AssociatedPageInfo {
    let clonedPageInfo = new AssociatedPageInfo();
    clonedPageInfo.notebookName = associatedPageInfo.notebookName;
    clonedPageInfo.sectionName = associatedPageInfo.sectionName;
    clonedPageInfo.pageName = associatedPageInfo.pageName;
    return clonedPageInfo;
  }
}

export const NavigateToAssociatedPage = async () => {
  await OneNote.run(async (context) => {
    try {
      const notebook = context.application.notebooks.getItem(HostAppSettings.GetSettingAsString(HostAppSettingName.AssociatedOneNoteNotebookId));
      const section = notebook.sections.getItem(HostAppSettings.GetSettingAsString(HostAppSettingName.AssociatedOneNoteSectionId));
      const page = section.pages.getItem(HostAppSettings.GetSettingAsString(HostAppSettingName.AssociatedOneNotePageId));

      // Open the page on OneNote side.
      context.application.navigateToPage(page);
    } catch (e) {
      throw new OneNotePageError(OneNotePageErrors.NavigateToAssociatedPageFailure, ErrorStringFromException(e));
    }
  });
}

export const InitializeOneNoteTranscriptionManager = () => {
  oneNoteTranscriptionManager.Initialize();
}

export const ShouldShowOneNotePageInfoBar = () => {
  //only show this bar for oneNote.
  return HostAppUtil.GetHostAppName() === HostAppName.ONENOTE;
}

export const ShouldShowOneNoteIdentitySwitchDialog = (primaryIdentityEmail : string | undefined) => {
  return primaryIdentityEmail != HostAppSettings.GetSettingAsString(HostAppSettingName.OneNoteNotebookIdentityEmail);
}

export const ShowOneNoteIdentitySwitchDialog = (primaryIdentityEmail : string | undefined)  => {
const associatedNoteBookName = oneNoteTranscriptionManager.GetAssociatedNoteBookName();
const activeIdentityEmail = primaryIdentityEmail == undefined || primaryIdentityEmail == null ? EMPTY_STRING : primaryIdentityEmail;
const notebookIdentityEmail = isNullOrUndefined(HostAppSettings.GetSettingAsString(HostAppSettingName.OneNoteNotebookIdentityEmail))
  ? EMPTY_STRING : HostAppSettings.GetSettingAsString(HostAppSettingName.OneNoteNotebookIdentityEmail);

let identitySwitchDialogInfoString = TranscriptionStrings.OneNoteAccountSwitchDialogBodyInfoText;
identitySwitchDialogInfoString = identitySwitchDialogInfoString.replace('{0}', '[' + activeIdentityEmail + ']');
identitySwitchDialogInfoString = identitySwitchDialogInfoString.replace('{1}', associatedNoteBookName);
identitySwitchDialogInfoString = identitySwitchDialogInfoString.replace('{2}', '[' + notebookIdentityEmail + ']');

const transcriptionAlertState: TranscriptionAlertState = {
  title: TranscriptionStrings.OneNoteAccountSwitchDialogHeaderText,
  information: identitySwitchDialogInfoString + '\n\n' + TranscriptionStrings.AccountSwitchDialogBodyRelaunchText,
  buttonInfo: { buttonType: TranscriptionAlertButtonType.LINK, linkText: TranscriptionStrings.TranscriptionPaneLearnLabel, hyperLink: LEARN_MORE_URL }
}

dispatcher.dispatch({
  type: TranscriptionAlertActions.ShowTranscriptionAlert,
  payload: transcriptionAlertState
});
}

const oneNoteTranscriptionManager = new OneNoteTranscriptionManager();
export default oneNoteTranscriptionManager;